
import { useState, useEffect, useRef } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from '@tanstack/react-query';
import { toast } from "sonner";
import { logger } from "@/utils/logger";

export const useAuthState = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const initAttempt = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    
    const setupSession = async () => {
      initAttempt.current += 1;
      logger.debug(`Setting up auth session, attempt ${initAttempt.current}`);
      
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Session setup error:', sessionError);
          if (mounted.current) {
            setError(sessionError);
            toast.error("Session error: " + sessionError.message);
          }
          return;
        }
        
        if (mounted.current) {
          logger.debug('Auth session setup complete:', session?.user?.id ? 'User logged in' : 'No user');
          setUser(session?.user ?? null);
        }
      } catch (error) {
        console.error('Session setup error:', error);
        if (mounted.current) {
          setError(error instanceof Error ? error : new Error('Failed to set up session'));
        }
      } finally {
        if (mounted.current) {
          setLoading(false);
        }
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        logger.debug('Auth state changed:', event, 'User ID:', session?.user?.id);
        if (mounted.current) {
          setUser(session?.user ?? null);
          setLoading(false);
        }
      }
    );

    setupSession();

    return () => {
      mounted.current = false;
      subscription.unsubscribe();
    };
  }, []);

  // Check if user is admin using React Query
  const { data: isAdmin = false } = useQuery({
    queryKey: ['isAdmin', user?.id],
    queryFn: async () => {
      if (!user) return false;
      
      // Use secure is_admin() RPC instead of direct user_roles query
      const { data: isAdmin, error } = await supabase.rpc('is_admin');
      
      if (error) {
        console.error('Error checking admin status');
        return false;
      }
      
      return !!isAdmin;
    },
    enabled: !!user,
  });

  // Fetch user profile using React Query
  const { data: profile = null } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user) return null;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching profile:', error);
        return null;
      }
      
      return data;
    },
    enabled: !!user,
    retry: 2,
    staleTime: 30000, // Data remains fresh for 30 seconds
  });

  return { user, loading, isAdmin, profile, error };
};
